import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  listProjects,
  createProject,
  deleteProject,
  duplicateProject,
  formatDuration,
  timeAgoShort,
  PROJECT_TYPE_LABEL,
  ASPECT_RATIOS,
  type ContentProject,
} from '@/lib/contentStudio';

const EXAMPLE_IDEAS = [
  { icon: 'ri-film-line', text: "Une histoire de 2 minutes sur l'histoire de Mobutu, style documentaire." },
  { icon: 'ri-megaphone-line', text: 'Une publicité vidéo de 30 secondes pour mon produit.' },
  { icon: 'ri-book-open-line', text: 'Un documentaire sur les grands royaumes africains.' },
  { icon: 'ri-emotion-laugh-line', text: 'Une histoire drôle de 2 minutes avec une narration vivante.' },
];

const DURATIONS = [
  { value: 15, label: '15 s' },
  { value: 30, label: '30 s' },
  { value: 60, label: '1 min' },
  { value: 120, label: '2 min' },
  { value: 180, label: '3 min' },
  { value: 300, label: '5 min' },
];

const LANGUAGES = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'Anglais' },
  { value: 'ar', label: 'Arabe' },
  { value: 'es', label: 'Espagnol' },
  { value: 'pt', label: 'Portugais' },
];

const STYLES = ['Documentaire', 'Cinématographique', 'Publicitaire', 'Pédagogique', 'Réseaux sociaux', 'Dynamique'];

export default function ContentStudioHomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [projects, setProjects] = useState<ContentProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ContentProject | null>(null);

  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(60);
  const [language, setLanguage] = useState('fr');
  const [style, setStyle] = useState('Documentaire');
  const [aspectRatio, setAspectRatio] = useState('16:9');

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const list = await listProjects(user.id);
      setProjects(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    if (!user?.id || generating) return;
    const idea = prompt.trim();
    if (!idea) return;
    setGenerating(true);
    setError(null);
    try {
      const title = idea.length > 58 ? `${idea.slice(0, 58)}…` : idea;
      const project = await createProject({
        ownerId: user.id,
        name: title || 'Nouveau projet',
        prompt: idea,
        duration,
        language,
        style,
        aspectRatio,
      });
      navigate(`/dashboard/content-studio/project/${project.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de créer le projet');
      setGenerating(false);
    }
  };

  const handleDuplicate = async (p: ContentProject) => {
    try {
      await duplicateProject(p);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de duplication');
    }
  };

  const handleDelete = async (p: ContentProject) => {
    try {
      await deleteProject(p.id);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de suppression');
    }
  };

  const statusMeta = (p: ContentProject) => {
    if (p.status === 'generating') return { label: 'Génération…', className: 'bg-primary-100 text-primary-700', icon: 'ri-loader-4-line animate-spin' };
    if (p.status === 'ready') return { label: 'Prêt', className: 'bg-accent-100 text-accent-700', icon: 'ri-checkbox-circle-line' };
    if (p.status === 'error') return { label: 'Erreur', className: 'bg-red-50 text-red-600', icon: 'ri-error-warning-line' };
    return { label: 'Brouillon', className: 'bg-background-100 text-foreground-500', icon: 'ri-draft-line' };
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-quill-pen-ai-line text-accent-500"></i>
            Zifek AI Content Studio
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Créez vidéos, scripts, storyboards et contenus multimédia à partir d'une simple idée.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/dashboard/content-studio/voice"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer no-underline"
          >
            <i className="ri-volume-up-line"></i>
            Text-to-Voice
          </Link>
          <Link
            to="/dashboard/content-studio/series"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-background-200/70 bg-background-50 text-sm font-medium text-foreground-700 whitespace-nowrap hover:border-accent-300 hover:text-foreground-900 transition-colors cursor-pointer"
          >
            <i className="ri-stack-line text-accent-500"></i>
            Séries
          </Link>
          <Link
            to="/dashboard/content-studio/calendar"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-background-200/70 bg-background-50 text-sm font-medium text-foreground-700 whitespace-nowrap hover:border-accent-300 hover:text-foreground-900 transition-colors cursor-pointer"
          >
            <i className="ri-calendar-2-line text-accent-500"></i>
            Calendrier
          </Link>
          <Link
            to="/dashboard/content-studio/media"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-background-200/70 bg-background-50 text-sm font-medium text-foreground-700 whitespace-nowrap hover:border-accent-300 hover:text-foreground-900 transition-colors cursor-pointer"
          >
            <i className="ri-gallery-line text-accent-500"></i>
            Bibliothèque média
          </Link>
        </div>
      </div>

      {/* Espace de création */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-6 mb-8">
        <label className="block text-sm font-semibold text-foreground-800 mb-2">
          Que voulez-vous créer ?
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          placeholder="Ex : Fais-moi une histoire de deux minutes sur l'histoire de Mobutu, avec une narration intéressante et un style documentaire."
          className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 resize-none"
        />

        {/* Idées rapides */}
        <div className="flex flex-wrap gap-2 mt-3">
          {EXAMPLE_IDEAS.map((ex) => (
            <button
              key={ex.text}
              type="button"
              onClick={() => setPrompt(ex.text)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-background-200/70 text-xs text-foreground-600 hover:bg-background-100 hover:border-accent-300 transition-colors cursor-pointer"
            >
              <i className={`${ex.icon} text-accent-500`}></i>
              <span className="max-w-[220px] truncate">{ex.text}</span>
            </button>
          ))}
        </div>

        {/* Options */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-5">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Durée</label>
            <div className="relative">
              <select
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="w-full appearance-none px-3 py-2.5 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer"
              >
                {DURATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Langue</label>
            <div className="relative">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full appearance-none px-3 py-2.5 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer"
              >
                {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Style</label>
            <div className="relative">
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                className="w-full appearance-none px-3 py-2.5 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer"
              >
                {STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Format</label>
            <div className="flex items-center gap-1 bg-background-100 rounded-md p-1">
              {ASPECT_RATIOS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setAspectRatio(r.value)}
                  title={r.hint}
                  className={`flex-1 px-2 py-1.5 rounded text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    aspectRatio === r.value ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 mt-5">
          <p className="text-xs text-foreground-400 flex items-center gap-1.5">
            <i className="ri-information-line"></i>
            L'IA analyse votre demande, rédige le script puis découpe les scènes.
          </p>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={!prompt.trim() || generating}
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            {generating ? <><i className="ri-loader-4-line animate-spin"></i>Création…</> : <><i className="ri-sparkling-2-line"></i>GÉNÉRER</>}
          </button>
        </div>

        {error && (
          <div className="mt-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
            <i className="ri-error-warning-line"></i>{error}
          </div>
        )}
      </div>

      {/* Mes projets récents */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-folders-line text-foreground-500"></i>
          Mes projets récents
        </h3>
        <span className="text-xs text-foreground-400">{projects.length} projet{projects.length > 1 ? 's' : ''}</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i>
        </div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-xl text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-movie-2-line text-2xl text-foreground-400"></i>
          </div>
          <h4 className="text-sm font-semibold text-foreground-700 mb-1">Aucun projet pour l'instant</h4>
          <p className="text-sm text-foreground-500 max-w-sm">
            Écrivez votre idée ci-dessus puis cliquez sur GÉNÉRER pour créer votre premier contenu.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => {
            const meta = statusMeta(p);
            return (
              <div
                key={p.id}
                className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-accent-300 transition-colors"
              >
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/content-studio/project/${p.id}`)}
                  className="w-full text-left cursor-pointer"
                >
                  <div className="relative h-32 bg-gradient-to-br from-background-100 to-background-200/60 flex items-center justify-center overflow-hidden">
                    {p.thumbnail_url ? (
                      <img src={p.thumbnail_url} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <i className="ri-film-line text-3xl text-foreground-300"></i>
                    )}
                    <span className={`absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.className}`}>
                      <i className={meta.icon}></i>{meta.label}
                    </span>
                    <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/50 text-white text-[10px] font-medium">
                      {p.aspect_ratio}
                    </span>
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-semibold text-foreground-900 truncate">{p.name}</h4>
                    <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-price-tag-3-line"></i>{PROJECT_TYPE_LABEL[p.type] || p.type}
                      </span>
                      <span className="text-foreground-300">·</span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-timer-line"></i>{formatDuration(p.duration_seconds)}
                      </span>
                    </div>
                    <p className="text-[11px] text-foreground-400 mt-1">Mis à jour {timeAgoShort(p.updated_at)}</p>
                  </div>
                </button>
                <div className="flex items-center border-t border-background-200/70 divide-x divide-background-200/70">
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/content-studio/project/${p.id}`)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-folder-open-line"></i>Ouvrir
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDuplicate(p)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-file-copy-line"></i>Dupliquer
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(p)}
                    className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-medium text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation suppression */}
      {confirmDelete && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[60]" onClick={() => setConfirmDelete(null)}></div>
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-full max-w-sm bg-background-50 rounded-2xl border border-background-200/70 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-delete-bin-line"></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground-950">Supprimer ce projet ?</h3>
                <p className="text-xs text-foreground-500 truncate">{confirmDelete.name}</p>
              </div>
            </div>
            <p className="text-xs text-foreground-500 mb-4">Cette action est définitive. Le script et les scènes du projet seront supprimés.</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 rounded-full border border-background-200/70 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="flex-1 py-2.5 rounded-full bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Supprimer
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}